import React from 'react';
import styles from './region-entity-table.module.scss';
import { connect } from 'react-redux';
import { humanizeNumber } from '../../helpers/formatters';
import * as selectors from '../../reducers';
import PropTypes from 'prop-types';
import {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
} from '../../actions/ui';
import { RegionTableHeader } from './region-table-header';
import { ArrowRight } from '../../components/icons';
import InfoIcon from '../info-tooltip-icon';
import { strings } from '../../helpers/strings';

// const cols = [
//   {
//     header: 'Distribution area',
//     key: 'distributionArea',
//     units: '',
//     footnote: '1',
//   },
//   {
//     header: 'Energy consumed',
//     key: 'energyConsumptionKwh',
//     units: 'kwh',
//     minWidth: 130,
//     tooltip: 'kwh',
//     footnote: '2',
//   },
//   {
//     header: 'Average annual household consumption',
//     key: 'averageConsumptionPerHouseholdKwh',
//     tooltip: 'kwh',
//     units: 'kwh',
//     footnote: '3',
//   },
//   {
//     header: 'Solar capacity',
//     key: 'rooftopSolarCapacityKva',
//     units: 'kva',
//     tooltip: 'kva',
//     minWidth: 120,
//     footnote: '4',
//   },
//   {
//     header: 'Households with rooftop solar',
//     key: 'percentOfRooftopSolar',
//     units: 'percentage',
//     footnote: '5',
//   },
//   {
//     header: 'Battery storage',
//     key: 'batteryStorageKvah',
//     units: 'kvah',
//     tooltip: 'kvah',
//     minWidth: 130,
//     footnote: '4',
//   },
// ];

const cols = [
  {
    header: 'Distribution area',
    key: 'distributionArea',
    units: '',
    footnote: '1',
  },
  {
    header: 'Typical annual residential consumption (KWh)',
    key: 'typicalAnnualResidentialElectricityConsumptionKWh',
    tooltip: 'kwh',
    units: 'KWh',
    footnote: '2',
  },
  {
    header: 'Households with rooftop solar',
    key: 'approxPortionOfHouseholdsWithRooftopSolar',
    units: 'percentage',
    footnote: '3',
  },
];

const footnotes = [
  {
    number: 1,
    text: 'Distribution areas sourced from Energex and Ergon service regions.',
  },
  {
    number: 2,
    text: (
      <span>
        Calculated based on a three person household and their climate zones.
        Data was obtained from the{' '}
        <a href="https://www.aer.gov.au/retail-markets/guidelines-reviews/electricity-and-gas-consumption-benchmarks-for-residential-customers-2020">
          Australian Energy Regulator
        </a>
        . Where the regions include multiple climate zones, we have averaged the
        typical electricity consumption between the zones.
      </span>
    ),
  },
  {
    number: 3,
    text: (
      <span>
        Calculated based on the number of rooftop solar connections divided by the
        number of households. Solar connection data is as of 30 June 2021.
        Data obtained from the{' '}
        <a href="https://pv-map.apvi.org.au/historical">
          Australian PV Institute
        </a>
        . Data has been extracted by Local Government Area (LGA) and
        subsequently mapped to the regions based on the ABS 2018 LGA to SA4/3
        mapping.
      </span>
    ),
  },
  // {
  //   number: 4,
  //   text:
  //     'Connection data has been sourced from the AEMO distributed energy resource register, current as at June 2020.',
  // },
  // {
  //   number: 5,
  //   text:
  //     'Connection data has been sourced from the AEMO distributed energy resource register, current as at June 2020. Divided by demographic data from the Australian Bureau of Statistics.',
  // },
  {
    number: 4,
    text:
      "State owned power station data sourced from Energy entities' published annual reports.",
  },
];

const RegionTableComponent = ({
  region,
  setActiveEntity,
  setComparedEntity,
  setActivePage,
  isMobile,
}) => {
  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '5px 0px',
        }}
      >
        <table className={styles.entityRegionTable}>
          <tbody>
            <RegionTableHeader
              region={region}
              comparing={false}
              isMobile={isMobile}
            />
            <tr className={styles.regionTableHeaderRow}>
              {cols.map((col, i) => (
                <td key={`header-${i}`}>
                  {col.header}
                  {col.footnote && <sup> {col.footnote}</sup>}
                </td>
              ))}
            </tr>
            <tr>
              {cols.map((col, i) => {
                return (
                  <td style={{ minWidth: col.minWidth }} key={`row-${i}`}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span>
                        {humanizeNumber(region[col.key], col.units) || 'N/A'}
                      </span>
                      {col.tooltip && region[col.key] && (
                        <InfoIcon tooltipText={strings[col.tooltip]} />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
        <h5 className={styles.facilityType}>
          Power stations <sup>4</sup>
        </h5>
        {region.byRegionByEntitybyFacility.length > 0 ? (
          [
            <ul className={styles.facilities}>
              {region.byRegionByEntitybyFacility
                .sort((a, b) =>
                  a.powerStation < b.powerStation
                    ? -1
                    : a.powerStation > b.powerStation
                    ? 1
                    : 0
                )
                .map((x, i) => {
                  return (
                    <li className={styles.facility} key={x.entity + i}>
                      <span>{`${x.powerStation} (`}</span>
                      <span
                        className={styles.facilityEntity}
                        onClick={() => {
                          setActiveEntity(x.entity);
                          setComparedEntity(null);
                          setActivePage('entity');
                        }}
                        data-clickable={true}
                      >
                        {x.entity === 'EEQ' ? "Ergon Energy Queensland" : x.entity}
                      </span>
                      <span>{`, ${x.powerStationType}, ${humanizeNumber(
                        x.capacityMw,
                        'mw'
                      )} `}</span>
                      <InfoIcon
                        tooltipText={strings.mw}
                        css={{ marginRight: 5 }}
                      />
                      <span>{` capacity)`}</span>
                    </li>
                  );
                })}
            </ul>,
          ]
        ) : (
          <p className={styles.cellInfoText}>
            There are no state owned power stations.
          </p>
        )}
        {region.name === 'Central Queensland' && (
          <p className={styles.cellInfoText}>
            CS Energy does not own Gladstone Power Station, however they are
            party to an agreement which allows them to trade any surplus output,
            after the daily requirements of the Boyne Aluminium Smelter have
            been met. Callide C is 50% owned by CS Energy.
          </p>
        )}
        {region.name === 'Mackay - Isaac - Whitsunday' && (
          <p className={styles.cellInfoText}>
            Stanwell has decommissioned the Mackay Gas Turbine into retirement as at 1 April 2021
          </p>
        )}
        <h5 className={styles.facilityType}>References</h5>
        <div className={styles.footnotesSection}>
          {footnotes.map((f) => (
            <p className={styles.cellInfoText} style={{ margin: 0 }}>
              <sup>{f.number}</sup> {f.text}
            </p>
          ))}
        </div>
      </div>
      {isMobile && (
        <div style={{ textAlign: 'left', marginTop: 20 }}>
          <span className={styles.cellInfoText}>
            Scroll the table to see more
          </span>
          <ArrowRight
            fill="blue"
            width="10px"
            height="10px"
            style={{ marginLeft: 10 }}
          />
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
};

const mapStateToProps = (state, props) => ({
  isMobile: selectors.getIsMobile(state),
});

RegionTableComponent.propTypes = {
  region: PropTypes.object,
  setActiveEntity: PropTypes.func,
  setActivePage: PropTypes.func,
  setComparedEntity: PropTypes.func,
};

const RegionTable = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegionTableComponent);
export { RegionTable };



// WEBPACK FOOTER //
// ./src/components/region-entity-table/region-table.js